import type { DexieKvStore } from './dexie-kv.js';

// The explicit allowlist (design §5 / task 3.5) — ONLY these query key
// prefixes are ever written to IndexedDB. Everything else (auth/token
// responses, anything not needed offline) is deliberately excluded so a
// dehydrated persisted cache can never leak a token-bearing response.
// Exact first-segment match only (see shouldPersistQuery) — no prefix
// substring matching, so a future 'checkin-config-v2' key does not
// accidentally inherit this allowlist entry.
//
// Adversarial-review fix (Slice 3, finding #1, HIGH/blocking):
// 'my-active' and 'retrieval-queue' were REMOVED. Their items
// (RetrievalQueueItemSchema) carry plate, qrTag, zone and paymentStatus for
// EVERY active ticket — they serve the pickup workflow, not offline
// check-in capture (the actually-accepted offline goal, spec
// pwa-offline-shell). Persisting them made that customer data readable
// with NO credentials from the offline-shell branch (task 3.6) for up to
// `maxAge` (24h). Only 'checkin-config' and 'comps-config' remain — both
// verified to carry no customer data: CheckinFormConfigResponseSchema is
// niche/field-toggle/ticket-types/tariff/comps CONFIG only (see
// packages/shared/src/ticket-schemas.ts). Any future addition to this list
// MUST be checked against the explicit denylist test in
// query-persister.spec.ts before merging.
export const ALLOWLISTED_QUERY_KEY_PREFIXES = [
  'checkin-config', // useCheckinConfig — mandatory photo slots, ticket-types config
  'comps-config', // useCompsConfig
] as const;

/**
 * The allowlist predicate wired into `persistQueryClient`'s
 * `dehydrateOptions.shouldDehydrateQuery` (see app/query-client.ts). Takes
 * TanStack Query's `queryKey` (always an array — `readonly unknown[]`) and
 * checks only its first segment against the allowlist above.
 */
export function shouldPersistQuery(queryKey: readonly unknown[]): boolean {
  const [first] = queryKey;
  if (typeof first !== 'string') return false;
  return (ALLOWLISTED_QUERY_KEY_PREFIXES as readonly string[]).includes(first);
}

// Minimal shape of the AsyncStorage contract
// `@tanstack/query-async-storage-persister`'s `createAsyncStoragePersister`
// expects — declared locally instead of importing the library's type so this
// module has zero build-time coupling to it (only app/query-client.ts does).
export interface AsyncStorageLike {
  getItem: (key: string) => Promise<string | null>;
  setItem: (key: string, value: string) => Promise<void>;
  removeItem: (key: string) => Promise<void>;
}

/**
 * Adapts the generic `DexieKvStore` port to the AsyncStorage shape the
 * persister expects. The persister already serializes the dehydrated
 * PersistedClient to a JSON string before calling `setItem` — this adapter
 * only needs to round-trip that string through the Dexie `kv` table under a
 * single fixed key (design §5 kv table content: `rq-cache`).
 */
export function createDexieAsyncStorage(kv: DexieKvStore): AsyncStorageLike {
  return {
    async getItem(key) {
      const value = await kv.get(key);
      return typeof value === 'string' ? value : null;
    },
    async setItem(key, value) {
      await kv.set(key, value);
    },
    async removeItem(key) {
      await kv.remove(key);
    },
  };
}
